/**
 * SopViewerPage — Read-only SOP hierarchy viewer (Phase 21.5.3)
 *
 * Displays the full SOP hierarchy: Jobs > Tasks > Steps
 * with collapsible accordions, safety-critical highlighting,
 * and estimated durations.
 *
 * Route: /operator/sops/:sopId
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ShieldAlert,
  Clock,
  FileText,
  Layers,
} from 'lucide-react';
import { useOperatorSopDetail } from '../../hooks/useOperatorPortal';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { OperatorLoadingSkeleton } from './components/OperatorLoadingSkeleton';
import { OperatorEmptyState } from './components/OperatorEmptyState';
import { SopVersionBanner } from './components/SopVersionBanner';
import { cn } from '../../lib/utils';

/** Format seconds to "~X min" label */
function formatDuration(seconds: number | null): string | null {
  if (seconds == null || seconds <= 0) return null;
  const mins = Math.round(seconds / 60);
  return mins < 1 ? '~1 min' : `~${mins} min`;
}

export function SopViewerPage() {
  const { sopId } = useParams<{ sopId: string }>();
  const { data: sop, isLoading, isError, error } = useOperatorSopDetail(sopId ?? null);

  useDocumentTitle(sop ? sop.title : 'SOP Reference');

  // Track which sections are expanded — first section starts expanded
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set());
  const [initialized, setInitialized] = useState(false);

  // Initialize expanded state when data arrives
  if (sop && !initialized && sop.sections.length > 0) {
    setExpandedSections(new Set([sop.sections[0].id]));
    setInitialized(true);
  }

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  // --- Loading ---
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Link
          to="/operator/content"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-op-text-secondary active:text-op-text-primary transition-colors"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back to Content
        </Link>
        <OperatorLoadingSkeleton variant="cards" />
      </div>
    );
  }

  // --- Error: SOP not found or deactivated ---
  if (isError || !sop) {
    const is404 = (error as { status?: number })?.status === 404;
    return (
      <div className="space-y-4">
        <Link
          to="/operator/content"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-op-text-secondary active:text-op-text-primary transition-colors"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back to Content
        </Link>
        <OperatorEmptyState
          icon={FileText}
          title={is404 ? 'SOP No Longer Available' : 'Unable to Load SOP'}
          description={
            is404
              ? 'This procedure may have been updated or removed. Go back to view available SOPs.'
              : 'Something went wrong loading this procedure. Please try again.'
          }
          ctaLabel="Back to Content"
          ctaHref="/operator/content"
        />
      </div>
    );
  }

  // --- Empty sections: SOP still processing ---
  if (sop.sections.length === 0) {
    return (
      <div className="space-y-4">
        <Link
          to="/operator/content"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-op-text-secondary active:text-op-text-primary transition-colors"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back to Content
        </Link>
        <OperatorEmptyState
          icon={Layers}
          title="Content Still Processing"
          description="This SOP's content is still being processed. Check back soon."
          ctaLabel="Back to Content"
          ctaHref="/operator/content"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Back link */}
      <Link
        to="/operator/content"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-op-text-secondary active:text-op-text-primary transition-colors"
      >
        <ArrowLeft size={16} strokeWidth={1.5} />
        Back to Content
      </Link>

      {/* Phase 23.2.7: "What changed" banner. Rendered only when the current
          operator hasn't acknowledged the SOP's current version. Dismissal
          persists server-side via operator_sop_acknowledgments. */}
      {sopId ? <SopVersionBanner sopId={sopId} /> : null}

      {/* SOP header */}
      <div style={{ animation: 'fadeInUp 0.4s ease-out' }}>
        <h1 className="text-lg font-semibold text-op-text-primary line-clamp-2">
          {sop.title}
        </h1>
        {sop.version && (
          <span className="mt-1 inline-block rounded-full bg-op-muted px-2.5 py-0.5 text-xs font-medium text-op-text-secondary">
            v{sop.version}
          </span>
        )}
        <p className="mt-2 text-xs text-op-text-tertiary">
          {sop.sections.length} {sop.sections.length === 1 ? 'job' : 'jobs'}
        </p>
      </div>

      {/* Job accordion list */}
      <div className="flex flex-col gap-2">
        {sop.sections.map((section, sectionIdx) => {
          const isExpanded = expandedSections.has(section.id);
          const hasSafetyCriticalTasks = section.tasks.some(
            (t) => t.isSafetyCritical || t.steps.some((s) => s.isSafetyCritical)
          );

          return (
            <div
              key={section.id}
              className="overflow-hidden rounded-2xl bg-op-card ring-1 ring-black/[0.04]"
              style={{ animation: `fadeInUp 0.4s ease-out ${0.06 * (sectionIdx + 1)}s both` }}
            >
              {/* Section header (Job) */}
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors duration-150',
                  'min-h-[48px]',
                  'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
                  'active:bg-gray-50'
                )}
                aria-expanded={isExpanded}
                aria-controls={`section-${section.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-op-text-tertiary">
                      Job {section.sectionNumber}
                    </span>
                    {hasSafetyCriticalTasks && (
                      <ShieldAlert size={14} strokeWidth={1.5} className="text-red-500" />
                    )}
                  </div>
                  <h3 className="mt-0.5 text-sm font-semibold text-op-text-primary line-clamp-2">
                    {section.title}
                  </h3>
                  {!isExpanded && (
                    <p className="mt-0.5 text-xs text-op-text-tertiary">
                      {section.tasks.length} {section.tasks.length === 1 ? 'task' : 'tasks'}
                    </p>
                  )}
                </div>
                <ChevronDown
                  size={18}
                  strokeWidth={1.5}
                  className={cn(
                    'flex-shrink-0 text-op-text-tertiary transition-transform duration-200',
                    isExpanded && 'rotate-180'
                  )}
                />
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div id={`section-${section.id}`} className="border-t border-black/[0.04] px-4 pb-4">
                  {section.description && (
                    <p className="mt-3 text-xs text-op-text-secondary leading-relaxed">
                      {section.description}
                    </p>
                  )}

                  {/* Tasks within section */}
                  {section.tasks.length === 0 ? (
                    <p className="mt-3 text-xs text-op-text-tertiary italic">
                      No tasks in this job yet.
                    </p>
                  ) : (
                    <div className="mt-3 flex flex-col gap-2">
                      {section.tasks.map((task) => (
                        <div
                          key={task.id}
                          className={cn(
                            'rounded-xl p-3',
                            task.isSafetyCritical ? 'bg-red-50 ring-1 ring-red-200' : 'bg-gray-50'
                          )}
                        >
                          {/* Task header */}
                          <div className="flex items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] font-semibold text-op-text-tertiary">
                                  Task {task.taskNumber}
                                </span>
                                {task.isSafetyCritical && (
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                                    <ShieldAlert size={10} strokeWidth={2} />
                                    Safety Critical
                                  </span>
                                )}
                              </div>
                              <h4 className="mt-0.5 text-sm font-medium text-op-text-primary">
                                {task.title}
                              </h4>
                            </div>
                            {task.estimatedDuration != null && (
                              <span className="flex items-center gap-1 whitespace-nowrap text-[11px] text-op-text-tertiary">
                                <Clock size={11} strokeWidth={1.5} />
                                {formatDuration(task.estimatedDuration)}
                              </span>
                            )}
                          </div>

                          {task.description && (
                            <p className="mt-1.5 text-xs text-op-text-secondary leading-relaxed">
                              {task.description}
                            </p>
                          )}

                          {/* Steps within task */}
                          {task.steps.length > 0 && (
                            <ol className="mt-2.5 flex flex-col gap-1.5">
                              {task.steps.map((step) => (
                                <li
                                  key={step.id}
                                  id={`step-${step.id}`}
                                  className={cn(
                                    'flex gap-2 rounded-lg px-2 py-1.5 text-xs scroll-mt-20',
                                    step.isSafetyCritical
                                      ? 'bg-red-100/60 text-red-800'
                                      : 'text-op-text-secondary'
                                  )}
                                >
                                  <span className="flex-shrink-0 font-semibold text-op-text-tertiary">
                                    {step.stepNumber}.
                                  </span>
                                  <span className="flex-1 leading-relaxed">
                                    {step.description}
                                    {step.isSafetyCritical && (
                                      <ShieldAlert
                                        size={12}
                                        strokeWidth={2}
                                        className="ml-1 inline-block text-red-500"
                                      />
                                    )}
                                  </span>
                                </li>
                              ))}
                            </ol>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
